/**
 * What an EMR entry's address can open, as TWO KEYS AND ONE DEFAULT.
 *
 * The workspace shows one thing at a time: the entry's diagnoses, one
 * protocol, the entry's referrals or its performed services. The choice is
 * in the URL rather than in component state so that a colleague can be sent
 * exactly what a clinician is looking at, and so that a reload does not
 * silently move them off the surface they had open.
 *
 * THE BARE ADDRESS IS THE DIAGNOSES. The diagnoses are the entry's
 * conclusions and the screen an opened entry lands on; each protocol is
 * addressed by `?protocol=CODE`, the entry's referrals by
 * `?section=referrals` and its performed services by `?section=services`.
 * A `section=diagnoses` would be a second spelling of the bare URL, so it does
 * not exist and is dropped like any other unusable value.
 *
 * PROTOCOL WINS when a link somehow carries both keys. No link this product
 * builds carries both; for a hand-edited one, keeping the protocol preserves
 * the more specific claim and drops the other rather than showing neither.
 *
 * Neither key carries authority. Every surface reads its own data through its
 * own capability-checked endpoint; these values change what is rendered and
 * never what may be read, so an unusable value is not a security event and
 * does not need to be an error page. It normalizes to the bare address.
 */

export const ENCOUNTER_REFERRALS_SECTION = 'referrals'
export const ENCOUNTER_SERVICES_SECTION = 'services'

export type EncounterSection =
  | typeof ENCOUNTER_REFERRALS_SECTION
  | typeof ENCOUNTER_SERVICES_SECTION

const ENCOUNTER_SECTIONS: readonly EncounterSection[] = [
  ENCOUNTER_REFERRALS_SECTION,
  ENCOUNTER_SERVICES_SECTION,
]

/** An entry-level section from whatever a link or a bookmark carried, or null. */
export function encounterSectionOf(value: unknown): EncounterSection | null {
  return ENCOUNTER_SECTIONS.find((section) => section === value) ?? null
}

/**
 * A definition code, only shaped rather than checked against the server's list.
 *
 * The protocols this encounter carries are an ANSWER, not something the router
 * can know while validating a URL, so this bounds the value to the shape a code
 * has and nothing more. A well-shaped code that no protocol on this entry uses
 * is refused BY THE WORKSPACE, by name, where the answer is already in hand.
 *
 * An empty result means the diagnoses — the bare address. There is therefore
 * exactly one address for the entry, whatever unusable text a link carried.
 */
const DEFINITION_CODE_RE = /^[a-z0-9_]{1,64}$/

export function encounterProtocolCode(value: unknown): string {
  return typeof value === 'string' && DEFINITION_CODE_RE.test(value) ? value : ''
}

/**
 * The search a link to one protocol carries — and, given an empty code, the
 * search that returns to the diagnoses.
 *
 * The key is OMITTED rather than emptied for the default: `?protocol=` and no
 * `protocol` at all would be two spellings of one screen.
 */
export function encounterProtocolSearch(code: string): { protocol?: string } {
  return code === '' ? {} : { protocol: code }
}

/**
 * The route's own validator.
 *
 * It drops rather than repairs. The arriving address keeps whatever a stale
 * link carried — the router rewrites nothing — but no accepted value ever
 * holds a dropped key, so no link the page builds echoes one onward. A link
 * written while the diagnoses needed `?section=diagnoses` lands on the
 * diagnoses, because that is now the bare address.
 *
 * `eventId` and `date` are carried through as strings and are not this module's
 * business: they identify the schedule entry the clinician arrived from, and
 * the surfaces that read them check them where they are used.
 */
export function validateEncounterSearch(search: Record<string, unknown>): {
  eventId?: string
  date?: string
  protocol?: string
  section?: EncounterSection
} {
  const result: {
    eventId?: string
    date?: string
    protocol?: string
    section?: EncounterSection
  } = {}
  if (typeof search['eventId'] === 'string') {
    result.eventId = search['eventId']
  }
  if (typeof search['date'] === 'string') {
    result.date = search['date']
  }
  const protocol = encounterProtocolCode(search['protocol'])
  const section = encounterSectionOf(search['section'])
  if (protocol !== '') {
    result.protocol = protocol
  } else if (section !== null) {
    result.section = section
  }
  return result
}
